import React, { useRef, useEffect } from "react";
import {
  StyleSheet,
  View,
  Modal,
  Animated,
  TouchableOpacity,
  useWindowDimensions,
  Platform,
} from "react-native";
import Icon from "../Icon";
import { useResponsive } from "../../hooks/useResponsive";
import { useAtom, useSetAtom } from "jotai";
import { deviceTypeAtom, refreshStreamCountAtom } from "../../atoms";
import { logEvent } from "../../client/analytics/trackPageView";

/**
 * AccessAndDevicesModal component renders a modal for managing access and devices
 * It uses React Native's Modal component and is a stripped back version of RModal - some of the styles
 * and behaviours in RModal are not suitable for this specific use case
 *
 * It includes animations for showing and hiding the modal
 *
 * The component is used in the ManageAccessAndDevices component when rendered as a modal so that
 * component should be used instead of this one directly
 *
 * @prop {boolean} visible - Whether the modal is visible
 * @prop {(visible: boolean) => void} setVisibility - Function to set the visibility of the modal
 * @prop {React.ReactNode} children - The content to be displayed inside the modal
 *
 * @returns {JSX.Element} The rendered AccessAndDevicesModal component
 *
 */

type Props = {
  visible: boolean;
  setVisibility: (visible: boolean) => void;
  children: React.ReactNode;
};

const AccessAndDevicesModal = ({ visible, setVisibility, children }: Props) => {
  const { height } = useWindowDimensions();
  const translateY = useRef(new Animated.Value(height)).current;
  const { isSmallAndDown } = useResponsive();
  const [deviceType] = useAtom(deviceTypeAtom);
  const setRefreshStreamCount = useSetAtom(refreshStreamCountAtom);
  const isTablet = deviceType === "TABLET";

  useEffect(() => {
    Animated.timing(translateY, {
      toValue: visible ? 0 : height,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [visible, height]);

  const handleClose = () => {
    setRefreshStreamCount((count) => count + 1);
    setVisibility(false);
    logEvent({ key: "device-management-modal-close", data: {} });
  };

  if (!visible) return null;

  return (
    <Modal
      transparent
      visible={visible}
      animationType="none"
      onRequestClose={handleClose}
    >
      <TouchableOpacity style={styles.overlay} onPress={handleClose} />

      <Animated.View
        style={[
          styles.container,
          !isTablet && {
            left: 0,
            right: 0,
          },
          {
            transform: [{ translateY }],
            width: isTablet ? 800 : undefined,
            maxWidth: isSmallAndDown ? "100%" : 600,
            alignSelf: isTablet ? "center" : "center",
            marginHorizontal:
              isSmallAndDown || Platform.OS != "web" ? 8 : "auto",
            justifyContent: isSmallAndDown ? undefined : "center",
            height: isSmallAndDown ? undefined : "100%",
          },
        ]}
      >
        <View style={styles.content}>
          <TouchableOpacity onPress={handleClose} style={styles.closeBtn}>
            <Icon
              name="close"
              size="medium"
              fill="palette.buttons.default.hovered.backgroundColor"
            />
          </TouchableOpacity>
          {children}
        </View>
      </Animated.View>
    </Modal>
  );
};

export default AccessAndDevicesModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "black",
    opacity: 0.5,
    position: "absolute",
    width: "100%",
    height: "100%",
  },
  container: {
    position: "absolute",
    ...Platform.select({
      web: {
        bottom: 70,
      },
      default: {
        bottom: 80,
      },
    }),
  },
  content: {
    backgroundColor: "white",
    borderRadius: 10,
  },
  closeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 1,
    padding: 12,
  },
});
